import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'ar' | 'en';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const translations: Record<Language, Record<string, string>> = {
  ar: {
    // Brands Marquee
    'brands.title': 'شركات كبرى تثق بنا',
    // Common
    'common.dir': 'rtl',
    'common.loading': 'جاري التحميل...',
    'common.error': 'حدث خطأ',
    'common.success': 'تم بنجاح',
    'common.retry': 'حاول مرة أخرى',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.back': 'رجوع',
    'common.next': 'التالي',
    'common.finish': 'إنهاء',
    'common.characters': 'حرف',
    'common.view': 'عرض',
    'common.continue': 'متابعة',
    'common.or': 'أو',
    // Announcement Banner
    'banner.pairing': '🚀 قريباً: ربط سهل عبر كود الاقتران',
    'banner.comingSoon': 'قريباً',
    // Navbar
    'nav.dashboard': 'لوحة التحكم',
    'nav.logout': 'تسجيل الخروج',
    'nav.login': 'تسجيل الدخول',
    'nav.getStarted': 'ابدأ الآن',
    'nav.home': 'الرئيسية',
    'nav.services': 'الخدمات',
    'nav.pricing': 'الأسعار',
    'nav.contact': 'تواصل معنا',
    // Home Page
    'home.badge': 'الجيل الجديد من الأتمتة',
    'home.title': 'أتمتة WhatsApp الذكية مع Operines',
    'home.subtitle': 'اربط واتساب بسهولة واستخدم الذكاء الاصطناعي لأتمتة محادثاتك، إدارة عملائك، وزيادة مبيعاتك على مدار الساعة.',
    'home.cta.start': 'ابدأ الآن مجاناً',
    'home.cta.login': 'تسجيل الدخول',
    'home.footer.rights': 'جميع الحقوق محفوظة',
    'home.footer.contact': 'تواصل معنا',
    'home.footer.email': 'البريد الإلكتروني',
    'home.footer.phone': 'الهاتف',
    // Stats
    'stats.messages': 'رسالة يومياً',
    'stats.customers': 'عميل نشط',
    'stats.uptime': 'وقت التشغيل',
    'stats.support': 'دعم متواصل',
    // Features
    'features.title': 'لماذا تختار Operines؟',
    'features.subtitle': 'نقدم لك أدوات قوية وسهلة الاستخدام لتحويل واتساب إلى قناة مبيعات ودعم فني متكاملة.',
    // Pricing
    'pricing.title': 'خطط الأسعار',
    'pricing.subtitle': 'اختر الخطة المناسبة لعملك',
    'pricing.monthly': 'شهرياً',
    'pricing.yearly': 'سنوياً',
    'pricing.trial': 'تجربة مجانية لمدة 7 أيام',
    'pricing.features': 'جميع الميزات',
    'pricing.support': 'دعم فني على مدار الساعة',
    'pricing.unlimited': 'رسائل غير محدودة',
    'pricing.ai': 'مساعد ذكاء اصطناعي',
    'pricing.integrations': 'تكاملات متعددة',
    'pricing.subscribe': 'اشترك الآن',
    'pricing.popular': 'الأكثر شعبية',
    'pricing.custom': 'خطة مخصصة للشركات',
    'pricing.customDesc': 'حلول مخصصة حسب احتياجاتك • أرقام واتساب غير محدودة',
    'pricing.contactUs': 'تواصل معنا',
    'pricing.save': 'وفر',
    'pricing.perMonth': 'شهر',
    'pricing.perYear': 'سنة',
    // Plans
    'plan.starter': 'المبتدئ',
    'plan.business': 'الأعمال',
    'plan.pro': 'المحترف',
    'plan.custom': 'مخصص',
    // Plan Features
    'feature.unlimitedMessages': 'رسائل غير محدودة',
    'feature.oneWhatsApp': 'رقم واتساب واحد',
    'feature.support247': 'دعم 24/7',
    'feature.prioritySupport': 'دعم ذو أولوية 24/7',
    'feature.vipSupport': 'دعم VIP 24/7',
    'feature.aiAssistant': 'مساعد ذكي',
    'feature.advancedAnalytics': 'تحليلات متقدمة',
    'feature.customTemplates': 'قوالب مخصصة',
    'feature.dedicatedManager': 'مدير مخصص',
    // Auth - Login
    'auth.login': 'تسجيل الدخول',
    'auth.loginSubtitle': 'أو أنشئ حساباً جديداً',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.rememberMe': 'تذكرني',
    'auth.forgotPassword': 'نسيت كلمة المرور؟',
    'auth.noAccount': 'ليس لديك حساب؟',
    'auth.createAccount': 'إنشاء حساب جديد',
    'auth.loginButton': 'تسجيل الدخول',
    'auth.emailNotRegistered': 'البريد الإلكتروني غير مسجل. يرجى إنشاء حساب جديد.',
    'auth.wrongPassword': 'كلمة المرور غير صحيحة',
    'auth.loginFailed': 'فشل تسجيل الدخول',
    'auth.clickHereToRegister': 'اضغط هنا لإنشاء حساب جديد',
    // Auth - Register
    'auth.register': 'إنشاء حساب جديد',
    'auth.registerSubtitle': 'أو سجل الدخول إلى حسابك الحالي',
    'auth.fullName': 'الاسم بالكامل',
    'auth.phone': 'رقم الهاتف',
    'auth.phonePlaceholder': 'رقم الهاتف',
    'auth.verificationMethod': 'طريقة التحقق',
    'auth.whatsapp': 'واتساب',
    'auth.emailMethod': 'إيميل',
    'auth.otpViaWhatsapp': 'سيتم إرسال رمز التحقق عبر واتساب',
    'auth.otpViaEmail': 'سيتم إرسال رمز التحقق عبر البريد الإلكتروني',
    'auth.freeTrial': '✨ تجربة مجانية لمدة 7 أيام',
    'auth.freeTrialActivated': '✨ تجربة مجانية لمدة 7 أيام مفعلة',
    'auth.registerSuccess': 'تم التسجيل بنجاح!',
    'auth.redirecting': 'جاري تحويلك إلى لوحة التحكم...',
    'auth.sentViaWhatsapp': 'تم الإرسال عبر واتساب',
    'auth.sentViaEmail': 'تم الإرسال عبر الإيميل',
    'auth.confirmPhone': 'تأكيد رقم الهاتف',
    'auth.confirmEmail': 'تأكيد البريد الإلكتروني',
    'auth.otpSendFailed': 'فشل في إرسال رمز التحقق',
    'auth.otpInvalid': 'رمز التحقق غير صحيح',
    'auth.verificationError': 'حدث خطأ أثناء التحقق',
    // Password
    'password.strength': 'قوة كلمة المرور',
    'password.weak': 'ضعيفة',
    'password.medium': 'متوسطة',
    'password.strong': 'قوية',
    'password.minLength': '8 أحرف على الأقل',
    'password.uppercase': 'حرف كبير واحد',
    'password.lowercase': 'حرف صغير واحد',
    'password.number': 'رقم واحد',
    'password.special': 'رمز خاص (!@#$%)',
    // Validation Errors
    'error.emailInvalid': 'البريد الإلكتروني غير صالح',
    'error.emailExists': 'البريد الإلكتروني مستخدم بالفعل',
    'error.phoneInvalidEg': 'رقم الهاتف غير صالح (يجب أن يبدأ بـ 01 ويكون 11 رقم)',
    'error.phoneInvalidAe': 'رقم الهاتف غير صالح (يجب أن يبدأ بـ 05 ويكون 10 أرقام)',
    'error.phoneExists': 'رقم الهاتف مستخدم بالفعل',
    'error.emailNotRegistered': 'البريد الإلكتروني غير مسجل في النظام',
    'error.general': 'حدث خطأ، يرجى المحاولة مرة أخرى',
    // Forgot Password
    'forgot.title': 'نسيت كلمة المرور؟',
    'forgot.subtitle': 'أدخل بريدك الإلكتروني وسنرسل لك كلمة مرور جديدة.',
    'forgot.sendButton': 'إرسال كلمة مرور جديدة',
    'forgot.successTitle': 'تم إرسال كلمة المرور الجديدة',
    'forgot.successMessage': 'تم إنشاء كلمة مرور جديدة وإرسالها إلى',
    'forgot.checkInbox': 'تحقق من صندوق الوارد أو مجلد الرسائل غير المرغوب فيها',
    'forgot.okay': 'حسناً، فهمت',
    // Dashboard
    'dashboard.welcome': 'مرحباً',
    'dashboard.overview': 'إليك نظرة عامة على خدماتك وحالة النظام.',
    'dashboard.connected': 'رقم واتساب متصل',
    'dashboard.instance': 'Instance',
    'dashboard.phone': 'رقم الهاتف',
    'dashboard.profile': 'اسم الملف الشخصي',
    'dashboard.status': 'الحالة',
    'dashboard.connected.status': 'متصل',
    'dashboard.disconnected.status': 'غير متصل',
    'dashboard.delete': 'حذف الاتصال',
    'dashboard.loading': 'جاري تحميل البيانات...',
    'dashboard.noData': 'لا توجد بيانات متاحة',
    'dashboard.title': 'لوحة تحكم ربط واتساب',
    'dashboard.subtitle': 'أكمل المهام التالية لإعداد مساعد واتساب الذكي',
    'dashboard.trialDaysLeft': 'متبقي {days} {unit} من التجربة المجانية',
    'dashboard.day': 'يوم',
    'dashboard.days': 'أيام',
    'dashboard.subscribeNow': 'اشترك الآن',
    'dashboard.subscribeMessage': 'اشترك في إحدى الخطط للاستمرار في استخدام الخدمة',
    // Tasks
    'task.connectWhatsApp': 'ربط رقم واتساب',
    'task.connectWhatsAppDesc': 'قم بربط رقم واتساب الخاص بك',
    'task.complete': 'مكتمل',
    'task.incomplete': 'غير مكتمل',
    'task.connectNow': 'ربط الآن',
    'task.reconnect': 'إعادة الربط',
    'task.configureAI': 'إعداد المساعد الذكي',
    'task.configureAIDesc': 'قم بتعيين رسالة النظام للمساعد الذكي',
    // Account Suspended
    'suspended.title': 'تم إيقاف حسابك',
    'suspended.subtitle': 'انتهت فترة التجربة المجانية',
    'suspended.trialEnded': 'انتهت الـ 7 أيام المجانية',
    'suspended.subscribeMessage': 'يرجى الاشتراك في إحدى الخطط للاستمرار',
    'suspended.contactMessage': 'للاستمرار في استخدام الخدمة، يرجى التواصل مع فريق الدعم لتفعيل اشتراكك',
    'suspended.contactMethods': 'طرق التواصل',
    'suspended.email': 'البريد الإلكتروني',
    'suspended.phoneEg': 'الهاتف (مصر)',
    'suspended.phoneAe': 'الهاتف (الإمارات)',
    'suspended.whatsappEg': 'واتساب (مصر)',
    'suspended.whatsappAe': 'واتساب (الإمارات)',
    'suspended.thanks': 'شكراً لاستخدامك Operines',
    'suspended.contactSupport': 'تواصل مع الدعم',
    // Subscription
    'subscription.title': 'اختر خطتك المثالية',
    'subscription.subtitle': 'اشترك الآن وارتقِ بأعمالك',
    'subscription.backToDashboard': 'العودة للوحة التحكم',
    'subscription.securePayment': 'دفع آمن',
    'subscription.cancelAnytime': 'إلغاء في أي وقت',
    'subscription.multiplePayment': 'طرق دفع متعددة',
    'subscription.support247': 'دعم 24/7',
    // Countries
    'country.egypt': 'مصر',
    'country.uae': 'الإمارات',
    // Services
    'service.whatsapp': 'مساعد WhatsApp الذكي',
    'service.whatsapp.desc': 'اربط رقمك وقم بإعداد الردود الذكية لأتمتة محادثاتك مع العملاء.',
    'service.telegram': 'مساعد تلجرام',
    'service.telegram.desc': 'اربط حساب تلجرام وأتمت الردود على رسائل العملاء.',
    'service.leads': 'الحصول على عملاء محتملين',
    'service.leads.desc': 'أدوات ذكية لجذب وتحويل العملاء المحتملين إلى عملاء فعليين.',
    'service.setup': 'إعداد الذكاء الاصطناعي',
    'service.connect': 'ربط WhatsApp',
    // System Message
    'system.title': 'إعداد الذكاء الاصطناعي',
    'system.subtitle': 'قم بتوجيه المساعد الذكي كيفية الرد على عملائك.',
    'system.label': 'رسالة النظام (System Message)',
    'system.help': 'هذه الرسالة تحدد شخصية المساعد الذكي والمعلومات التي يجب أن يعرفها عن عملك.',
    'system.warning': 'يجب ربط رقم واتساب أولاً قبل حفظ إعدادات الذكاء الاصطناعي',
    'system.cancel': 'إلغاء',
    'system.save': 'حفظ الإعدادات',
    // Integrations
    'integrations.title': 'التكاملات',
    'integrations.subtitle': 'اربط خدماتك الخارجية مع Operines',
    'integrations.googlesheets.desc': 'اربط جداول بيانات Google لحفظ وإدارة بيانات العملاء تلقائياً',
    'integrations.googlesheets.help': 'انسخ هذا الرابط واستخدمه في إعدادات Google OAuth',
    'integrations.googlesheets.clientId': 'أدخل Client ID من Google Console',
    'integrations.googlesheets.clientSecret': 'أدخل Client Secret من Google Console',
    'integrations.shopify.desc': 'اربط متجر Shopify لإدارة الطلبات والمنتجات عبر WhatsApp',
    'integrations.shopify.storeUrl': 'مثال: mystore.myshopify.com',
    'integrations.shopify.accessToken': 'أدخل Access Token من Shopify Admin',
    'integrations.available': 'التكاملات المتاحة',
    'integrations.availableInHigher': 'متاح في الخطط الأعلى',
    'integrations.upgradeMessage': 'قم بالترقية للحصول على المزيد من التكاملات والميزات المتقدمة'
  },
  en: {
    // Brands Marquee
    'brands.title': 'Leading Brands Trust Us',
    // Common
    'common.dir': 'ltr',
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.success': 'Success',
    'common.retry': 'Try Again',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.finish': 'Finish',
    'common.characters': 'characters',
    'common.view': 'View',
    'common.continue': 'Continue',
    'common.or': 'or',
    // Announcement Banner
    'banner.pairing': '🚀 Coming Soon: Easy connection via pairing code',
    'banner.comingSoon': 'Coming Soon',
    // Navbar
    'nav.dashboard': 'Dashboard',
    'nav.logout': 'Logout',
    'nav.login': 'Login',
    'nav.getStarted': 'Get Started',
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.pricing': 'Pricing',
    'nav.contact': 'Contact Us',
    // Home Page
    'home.badge': 'Next Generation Automation',
    'home.title': 'Smart WhatsApp Automation with Operines',
    'home.subtitle': 'Connect WhatsApp easily and use AI to automate your conversations, manage customers, and increase sales 24/7.',
    'home.cta.start': 'Start Free Trial',
    'home.cta.login': 'Login',
    'home.footer.rights': 'All rights reserved',
    'home.footer.contact': 'Contact Us',
    'home.footer.email': 'Email',
    'home.footer.phone': 'Phone',
    // Stats
    'stats.messages': 'Messages Daily',
    'stats.customers': 'Active Customers',
    'stats.uptime': 'Uptime',
    'stats.support': '24/7 Support',
    // Features
    'features.title': 'Why Choose Operines?',
    'features.subtitle': 'We provide powerful and easy-to-use tools to transform WhatsApp into a complete sales and support channel.',
    // Pricing
    'pricing.title': 'Pricing Plans',
    'pricing.subtitle': 'Choose the plan that fits your business',
    'pricing.monthly': 'Monthly',
    'pricing.yearly': 'Yearly',
    'pricing.trial': '7-day free trial',
    'pricing.features': 'All features included',
    'pricing.support': '24/7 technical support',
    'pricing.unlimited': 'Unlimited messages',
    'pricing.ai': 'AI Assistant',
    'pricing.integrations': 'Multiple integrations',
    'pricing.subscribe': 'Subscribe Now',
    'pricing.popular': 'Most Popular',
    'pricing.custom': 'Custom Enterprise Plan',
    'pricing.customDesc': 'Custom solutions for your needs • Unlimited WhatsApp numbers',
    'pricing.contactUs': 'Contact Us',
    'pricing.save': 'Save',
    'pricing.perMonth': 'month',
    'pricing.perYear': 'year',
    // Plans
    'plan.starter': 'Starter',
    'plan.business': 'Business',
    'plan.pro': 'Pro',
    'plan.custom': 'Custom',
    // Plan Features
    'feature.unlimitedMessages': 'Unlimited Messages',
    'feature.oneWhatsApp': '1 WhatsApp Number',
    'feature.support247': '24/7 Support',
    'feature.prioritySupport': '24/7 Priority Support',
    'feature.vipSupport': '24/7 VIP Support',
    'feature.aiAssistant': 'AI Assistant',
    'feature.advancedAnalytics': 'Advanced Analytics',
    'feature.customTemplates': 'Custom Templates',
    'feature.dedicatedManager': 'Dedicated Manager',
    // Auth - Login
    'auth.login': 'Login',
    'auth.loginSubtitle': 'or create a new account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.rememberMe': 'Remember me',
    'auth.forgotPassword': 'Forgot password?',
    'auth.noAccount': "Don't have an account?",
    'auth.createAccount': 'Create New Account',
    'auth.loginButton': 'Login',
    'auth.emailNotRegistered': 'Email not registered. Please create a new account.',
    'auth.wrongPassword': 'Incorrect password',
    'auth.loginFailed': 'Login failed',
    'auth.clickHereToRegister': 'Click here to create a new account',
    // Auth - Register
    'auth.register': 'Create New Account',
    'auth.registerSubtitle': 'or login to your existing account',
    'auth.fullName': 'Full Name',
    'auth.phone': 'Phone Number',
    'auth.phonePlaceholder': 'Phone Number',
    'auth.verificationMethod': 'Verification Method',
    'auth.whatsapp': 'WhatsApp',
    'auth.emailMethod': 'Email',
    'auth.otpViaWhatsapp': 'Verification code will be sent via WhatsApp',
    'auth.otpViaEmail': 'Verification code will be sent via email',
    'auth.freeTrial': '✨ 7 Days Free Trial',
    'auth.freeTrialActivated': '✨ 7 Days Free Trial Activated',
    'auth.registerSuccess': 'Registration Successful!',
    'auth.redirecting': 'Redirecting to dashboard...',
    'auth.sentViaWhatsapp': 'Sent via WhatsApp',
    'auth.sentViaEmail': 'Sent via Email',
    'auth.confirmPhone': 'Confirm Phone Number',
    'auth.confirmEmail': 'Confirm Email',
    'auth.otpSendFailed': 'Failed to send verification code',
    'auth.otpInvalid': 'Invalid verification code',
    'auth.verificationError': 'Verification error occurred',
    // Password
    'password.strength': 'Password Strength',
    'password.weak': 'Weak',
    'password.medium': 'Medium',
    'password.strong': 'Strong',
    'password.minLength': 'At least 8 characters',
    'password.uppercase': 'One uppercase letter',
    'password.lowercase': 'One lowercase letter',
    'password.number': 'One number',
    'password.special': 'One special character (!@#$%)',
    // Validation Errors
    'error.emailInvalid': 'Invalid email address',
    'error.emailExists': 'Email already in use',
    'error.phoneInvalidEg': 'Invalid phone number (must start with 01 and be 11 digits)',
    'error.phoneInvalidAe': 'Invalid phone number (must start with 05 and be 10 digits)',
    'error.phoneExists': 'Phone number already in use',
    'error.emailNotRegistered': 'Email not registered in the system',
    'error.general': 'An error occurred, please try again',
    // Forgot Password
    'forgot.title': 'Forgot Password?',
    'forgot.subtitle': 'Enter your email and we will send you a new password.',
    'forgot.sendButton': 'Send New Password',
    'forgot.successTitle': 'New Password Sent',
    'forgot.successMessage': 'A new password has been created and sent to',
    'forgot.checkInbox': 'Check your inbox or spam folder',
    'forgot.okay': 'Okay, Got it',
    // Dashboard
    'dashboard.welcome': 'Welcome',
    'dashboard.overview': "Here's an overview of your services and system status.",
    'dashboard.connected': 'WhatsApp Connected',
    'dashboard.instance': 'Instance',
    'dashboard.phone': 'Phone Number',
    'dashboard.profile': 'Profile Name',
    'dashboard.status': 'Status',
    'dashboard.connected.status': 'Connected',
    'dashboard.disconnected.status': 'Disconnected',
    'dashboard.delete': 'Delete Connection',
    'dashboard.loading': 'Loading data...',
    'dashboard.noData': 'No data available',
    'dashboard.title': 'WhatsApp Connection Dashboard',
    'dashboard.subtitle': 'Complete the following tasks to set up your WhatsApp AI assistant',
    'dashboard.trialDaysLeft': '{days} {unit} left in free trial',
    'dashboard.day': 'day',
    'dashboard.days': 'days',
    'dashboard.subscribeNow': 'Subscribe Now',
    'dashboard.subscribeMessage': 'Subscribe to a plan to continue using the service',
    // Tasks
    'task.connectWhatsApp': 'Connect WhatsApp Number',
    'task.connectWhatsAppDesc': 'Connect your WhatsApp number',
    'task.complete': 'Complete',
    'task.incomplete': 'Incomplete',
    'task.connectNow': 'Connect Now',
    'task.reconnect': 'Reconnect',
    'task.configureAI': 'Configure AI Assistant',
    'task.configureAIDesc': 'Set up the system message for AI assistant',
    // Account Suspended
    'suspended.title': 'Account Suspended',
    'suspended.subtitle': 'Your free trial period has ended',
    'suspended.trialEnded': '7 Days Free Trial Ended',
    'suspended.subscribeMessage': 'Please subscribe to a plan to continue',
    'suspended.contactMessage': 'To continue using the service, please contact our support team to activate your subscription',
    'suspended.contactMethods': 'Contact Methods',
    'suspended.email': 'Email',
    'suspended.phoneEg': 'Phone (Egypt)',
    'suspended.phoneAe': 'Phone (UAE)',
    'suspended.whatsappEg': 'WhatsApp (Egypt)',
    'suspended.whatsappAe': 'WhatsApp (UAE)',
    'suspended.thanks': 'Thank you for using Operines',
    'suspended.contactSupport': 'Contact Support',
    // Subscription
    'subscription.title': 'Choose Your Perfect Plan',
    'subscription.subtitle': 'Subscribe now and elevate your business',
    'subscription.backToDashboard': 'Back to Dashboard',
    'subscription.securePayment': 'Secure Payment',
    'subscription.cancelAnytime': 'Cancel Anytime',
    'subscription.multiplePayment': 'Multiple Payment Methods',
    'subscription.support247': '24/7 Support',
    // Countries
    'country.egypt': 'Egypt',
    'country.uae': 'UAE',
    // Services
    'service.whatsapp': 'WhatsApp AI Assistant',
    'service.whatsapp.desc': 'Connect your number and set up smart replies to automate conversations with customers.',
    'service.telegram': 'Telegram Assistant',
    'service.telegram.desc': 'Connect Telegram account and automate customer message responses.',
    'service.leads': 'Lead Generation',
    'service.leads.desc': 'Smart tools to attract and convert potential customers into actual customers.',
    'service.setup': 'Setup AI',
    'service.connect': 'Connect WhatsApp',
    // System Message
    'system.title': 'AI Setup',
    'system.subtitle': 'Guide your AI assistant on how to respond to customers.',
    'system.label': 'System Message',
    'system.help': "This message defines the AI assistant's personality and information about your business.",
    'system.warning': 'You must connect WhatsApp first before saving AI settings',
    'system.cancel': 'Cancel',
    'system.save': 'Save Settings',
    // Integrations
    'integrations.title': 'Integrations',
    'integrations.subtitle': 'Connect your external services with Operines',
    'integrations.googlesheets.desc': 'Connect Google Sheets to automatically save and manage customer data',
    'integrations.googlesheets.help': 'Copy this URL and use it in Google OAuth settings',
    'integrations.googlesheets.clientId': 'Enter Client ID from Google Console',
    'integrations.googlesheets.clientSecret': 'Enter Client Secret from Google Console',
    'integrations.shopify.desc': 'Connect Shopify store to manage orders and products via WhatsApp',
    'integrations.shopify.storeUrl': 'Example: mystore.myshopify.com',
    'integrations.shopify.accessToken': 'Enter Access Token from Shopify Admin',
    'integrations.available': 'Available Integrations',
    'integrations.availableInHigher': 'Available in higher plans',
    'integrations.upgradeMessage': 'Upgrade to unlock more integrations and advanced features'
  }
};
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    if (saved === 'ar' || saved === 'en') return saved as Language;
    // Auto-detect from browser language
    try {
      const browserLangs = navigator.languages || [navigator.language];
      const isArabic = browserLangs.some(lang => lang.toLowerCase().startsWith('ar'));
      return isArabic ? 'ar' : 'en';
    } catch {
      return 'ar'; // fallback
    }
  });
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);
  const t = (key: string): string => {
    return translations[language][key] || key;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-691">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}